// pages/ClientsPage.tsx
import { useState } from "react";
import { Search, Plus, Eye, MessageCircle, Phone } from "lucide-react";
import ClientList from "../../../components/features/clients/ClientsList";
import ClientDetailsModal from "../../../components/features/clients/ClientDetailsModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import useClients from "../../../hooks/features/clients/useClients";
import useTickets from "../../../hooks/features/tickets/useTickets";
import useCalls from "../../../hooks/features/calls/useCalls";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { BaseClient, Client } from "../../../types/features/Client";
import PageListLayout from "../../../layouts/PageListLayout";
import { useNavigate } from "react-router-dom";
import UpdateClientModal from "../../../components/features/clients/UpdateClientModal";
import CreateClientModal from "../../../components/features/clients/ClientCreateModal";

export default function ClientsPage() {
  const { clients, searchClients, searchResults, searchClientByPhone, clearSearch, loading, createClient, updateClient } = useClients();
  const navigate = useNavigate();
  const { tickets } = useTickets();
  const { calls } = useCalls();
  const [searchQuery, setSearchQuery] = useState("");

  // États de sélection
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientToView, setClientToView] = useState<Client | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  
  // États pour les modales de création et mise à jour
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [clientToUpdate, setClientToUpdate] = useState<Client | null>(null);

  const displayClients = searchQuery.trim() ? searchResults : clients;

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      await searchClients(searchQuery);
    } else {
      clearSearch();
    }
  };

  const handlePhoneSearch = async () => {
    if (searchQuery.trim()) {
      const client = await searchClientByPhone(searchQuery);
      if (client) {
        setClientToView(client);
        setDetailsModalOpen(true);
      }
      setSearchQuery("");
      clearSearch();
    }
  };

  const getClientCalls = (clientId: string) => {
    return calls.filter((call) => call.client_id === clientId).slice(0, 5);
  };

  const getClientTickets = (clientId: string) => {
    return tickets
      .filter((ticket) => ticket.client_id === clientId && ticket.status !== "resolu" && ticket.status !== "ferme")
      .slice(0, 5);
  };

  // Gestionnaires pour les actions sur les clients
  const handleCreateClient = async (clientData: BaseClient) => {
    try {
      await createClient(clientData);
      alertSuccess("Client créé avec succès !");
      setCreateModalOpen(false);
      // Rafraîchir la liste des clients
      await searchClients("");
    } catch (error) {
      alertError("Erreur lors de la création du client");
      console.error(error);
    }
  };

  const handleUpdateClient = async (clientData: Partial<Client>) => {
    if (!clientData.id) {
      alertError("Une erreur s'est produite")
      return 
    }
    try {
      await updateClient(clientData.id, clientData);
      alertSuccess("Client mis à jour avec succès !");
      setUpdateModalOpen(false);
      setClientToUpdate(null);
      // Rafraîchir la liste des clients
      await searchClients("");
    } catch (error) {
      alertError("Erreur lors de la mise à jour du client");
      console.error(error);
    }
  };

  const getActionsMenu = (client: Client): Menu[] => {
    return [
      {
        label: "Modifier",
        icon: Plus,
        onClick: () => {
          setClientToUpdate(client);
          setUpdateModalOpen(true);
          setSelectedClient(null);
        },
      },
      {
        label: "Discuter",
        icon: MessageCircle,
        onClick: () => {
          navigate("/support/chat", { state: { userId: client.user_id } });
          setSelectedClient(null);
        },
      },
      {
        label: "Créer ticket",
        icon: Plus,
        onClick: () => {
          navigate("/support/tickets/new", { state: { clientId: client.id } });
          setSelectedClient(null);
        },
      },
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          navigate("/support/clients/details", { state: client });
          setSelectedClient(null);
        },
      },
    ];
  };

  const isAnySubModalOpen = detailsModalOpen || updateModalOpen || createModalOpen;

  return (
    <PageListLayout title="">
      <div className="px-6 py-5 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-[#031B4E] tracking-tight">Clients</h1>
              <p className="text-sm text-slate-400 font-light mt-0.5">
                Recherchez et consultez les informations de votre répertoire clients.
              </p>
            </div>
            
            {/* Bouton pour créer un nouveau client */}
            <button
              onClick={() => setCreateModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Plus size={18} />
              <span>Nouveau client</span>
            </button>
          </div>

          {/* Zone de Recherche Épurée */}
          <div className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email ou téléphone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                />
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleSearch}
                  className="flex-1 sm:flex-none px-5 py-2.5 bg-primary text-white font-medium text-sm rounded-xl hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all shadow-sm"
                >
                  Rechercher
                </button>
                <button
                  onClick={handlePhoneSearch}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-5 py-2.5 bg-[#031B4E] text-white font-medium text-sm rounded-xl hover:bg-[#052975] focus:outline-none focus:ring-4 focus:ring-blue-600/10 transition-all shadow-sm"
                >
                  <Phone size={14} />
                  <span>Par téléphone</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des clients */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-[#031B4E] border-t-transparent"></div>
          </div>
        ) : (
          <ClientList
            clients={displayClients}
            onAction={(client) => {
              setSelectedClient(client);
            }}
            selectable={false}
          />
        )}
      </div>

      {/* Menu modal volant */}
      {selectedClient && !isAnySubModalOpen && (
        <MenuModal
          title={`${selectedClient.full_name || "Actions client"}`}
          isOpen={true}
          onClose={() => setSelectedClient(null)}
          icon={null}
          menu={getActionsMenu(selectedClient)}
        />
      )}

      {/* Modal détails client */}
      <ClientDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setClientToView(null);
        }}
        client={clientToView}
        recentCalls={clientToView ? getClientCalls(clientToView.id) : []}
        recentTickets={clientToView ? getClientTickets(clientToView.id) : []}
        onNewTicket={(clientId) => {
          setDetailsModalOpen(false);
          navigate("/support/tickets/new", { state: { clientId } });
        }}
        onCall={(phone) => {
          alertSuccess(`Appel vers ${phone}`);
        }}
      />

      {/* Modal de création de client */}
      <CreateClientModal
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
        }}
        onSuccess={handleCreateClient}
      />

      {/* Modal de mise à jour de client */}
      {clientToUpdate && (
        <UpdateClientModal
          isOpen={updateModalOpen}
          onClose={() => {
            setUpdateModalOpen(false);
            setClientToUpdate(null);
          }}
          onSuccess={handleUpdateClient}
          clientData={clientToUpdate}
          clientId={clientToUpdate.id}
        />
      )}
    </PageListLayout>
  );
}